import type { HTMLAttributes, ReactNode } from 'react';
import React, { useMemo } from 'react';
import classNames from 'classnames';
import merge from 'lodash/merge';
import type { ContentComponent } from '$cms/ContentComponent';
import { Row, Col } from '$src-components/atoms/Grid';
import type { MediaBlockProps } from '$quantum/molecules/MediaBlock';
import { MediaBlock } from '$quantum/molecules/MediaBlock';
import { HeadingLevels } from '@/components/atoms/HeadingLevels';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import type { SideBySideMediaBaseConfiguration, SideBySideMediaBaseTranslatedEntities } from './schema';
import { SideBySideMediaBaseSchema, defaultConfiguration } from './schema';
import styles from './index.module.scss';

const getOrder = (mediaColumn: number) => (mediaColumn % 2 === 0 ? 3 : 1);

export interface SideBySideMediaBaseProps extends HTMLAttributes<HTMLElement> {
  /**
   * This will be rendered in the "text" column.
   */
  readonly children: ReactNode;
}

/**
 * Component that renders Media and dynamic content columns.<br />
 * This is the common functionality shared between `SideBySideMediaText` and `SideBySideMediaAccordion`.
 */
export const SideBySideMediaBase: ContentComponent<
  SideBySideMediaBaseTranslatedEntities,
  SideBySideMediaBaseConfiguration
> = function SideBySideMediaText({ translatedEntities, configuration, children, className }) {
  const { heading, subheading, image, videoUrl, videoTitle, videoTag } = translatedEntities;
  const { headingsPosition, mediaColumn, accordionOrMediaRatio, textRatio, colors } = merge<
    Required<SideBySideMediaBaseConfiguration>,
    SideBySideMediaBaseConfiguration | undefined
  >(defaultConfiguration, configuration);

  const [orderCol1, orderCol2] = useMemo(
    () => [getOrder(mediaColumn), getOrder(mediaColumn + 1)],
    [mediaColumn]
  );

  const video: MediaBlockProps['video'] = useMemo(
    () =>
      videoUrl && {
        url: videoUrl.url,
        title: videoTitle?.value,
        tag: videoTag?.value,
      },
    [videoTag?.value, videoTitle?.value, videoUrl]
  );

  const shouldInsertOneColumnBetween = accordionOrMediaRatio + textRatio < 12;

  return (
    <ContentComponentSection
      className={classNames(SideBySideMediaText.displayName, styles.container, className)}
      style={{ backgroundColor: colors.backgroundColor, color: colors.textColor }}
    >
      {headingsPosition === 'top-center' && (
        <div className={styles.Header}>
          <HeadingLevels
            className={styles.Heading}
            semanticLevel={2}
            styleLevel={2}
            style={{ color: colors.titleColor }}
          >
            {heading.value}
          </HeadingLevels>
          <HeadingLevels
            className={styles.Subheading}
            semanticLevel={3}
            styleLevel={3}
            style={{ color: colors.subtitleColor }}
          >
            {subheading?.value}
          </HeadingLevels>
        </div>
      )}
      <Row className={styles.contentRow}>
        <Col
          className={classNames(styles.media, styles[`-column${mediaColumn}`], !image && styles.noImage)}
          extraSmall={4}
          small={6}
          medium={accordionOrMediaRatio}
          order={orderCol1}
        >
          <div className={styles.desktopHeading}>
            {headingsPosition === 'media-column' && (
              <div className={styles.desktopHeading}>
                <HeadingLevels
                  className={classNames(styles.Heading, styles.leftAlignedHeading)}
                  semanticLevel={2}
                  styleLevel={2}
                  style={{ color: colors.titleColor }}
                >
                  {heading.value}
                </HeadingLevels>
                <HeadingLevels
                  className={styles.Subheading}
                  semanticLevel={3}
                  styleLevel={3}
                  style={{ color: colors.subtitleColor }}
                >
                  {subheading?.value}
                </HeadingLevels>
              </div>
            )}
            {image && <MediaBlock image={image} video={video} />}
          </div>
        </Col>
        {shouldInsertOneColumnBetween && (
          <Col className={styles.spacer} medium={1} extraSmall={0} order={2}>
            {null}
          </Col>
        )}
        <Col className={styles.text} extraSmall={4} small={6} medium={textRatio} order={orderCol2}>
          {(headingsPosition === 'text-column' || headingsPosition === 'media-column') && (
            <div className={styles.mobileHeading}>
              <HeadingLevels
                className={styles.Heading}
                semanticLevel={2}
                styleLevel={2}
                style={{ color: colors.titleColor }}
              >
                {heading.value}
              </HeadingLevels>
              <HeadingLevels
                className={styles.Subheading}
                semanticLevel={3}
                styleLevel={3}
                style={{ color: colors.subtitleColor }}
              >
                {subheading?.value}
              </HeadingLevels>
            </div>
          )}
          {children}
        </Col>
      </Row>
    </ContentComponentSection>
  );
};

SideBySideMediaBase.displayName = 'SideBySideMediaBase';
SideBySideMediaBase.schema = SideBySideMediaBaseSchema;
